import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, Menu, Leaf, X } from "lucide-react";
import { useState } from "react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/catalogue", label: "Shop" },
  { to: "/stores", label: "Stores" },
  { to: "/franchise", label: "Franchise" },
  { to: "/about", label: "About" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobile, setMobile] = useState(false);
  const isStaff = user && user.role !== "customer";

  return (
    <header className="sticky top-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
          <span className="grid place-items-center w-9 h-9 rounded-xl bg-yellow-400 text-slate-900">
            <Leaf className="w-5 h-5" />
          </span>
          <span className="font-heading font-extrabold text-lg tracking-tight text-slate-900 dark:text-white">
            D K <span className="text-yellow-600">BAZAAR</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "text-yellow-700 bg-yellow-100 dark:bg-yellow-950 dark:text-yellow-300" : "text-slate-600 dark:text-slate-300 hover:text-yellow-700 hover:bg-yellow-100/60 dark:hover:bg-slate-800"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="open-cart-btn"
            onClick={() => setOpen(true)}
            className="relative p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <ShoppingCart className="w-5 h-5 text-slate-700 dark:text-slate-200" />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-1 -right-1 bg-orange-600 text-white text-[10px] font-bold w-5 h-5 grid place-items-center rounded-full">
                {count}
              </span>
            )}
          </button>

          {isStaff ? (
            <Button data-testid="portal-link-btn" onClick={() => navigate("/portal")} className="hidden sm:inline-flex bg-yellow-400 hover:bg-yellow-500 text-slate-900">
              My Portal
            </Button>
          ) : user ? (
            <Button data-testid="signout-btn" onClick={() => { logout(); navigate("/"); }} variant="outline" className="hidden sm:inline-flex border-yellow-500 text-yellow-700 hover:bg-yellow-100">
              Sign Out
            </Button>
          ) : (
            <Button data-testid="login-link-btn" onClick={() => navigate("/login")} variant="outline" className="hidden sm:inline-flex border-yellow-500 text-yellow-700 hover:bg-yellow-100">
              Sign In
            </Button>
          )}

          <button data-testid="mobile-menu-btn" className="lg:hidden p-2" onClick={() => setMobile((m) => !m)}>
            {mobile ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobile && (
        <div className="lg:hidden border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3 space-y-1">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} onClick={() => setMobile(false)}
              className="block px-3 py-2 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-yellow-100 dark:hover:bg-slate-800">
              {l.label}
            </NavLink>
          ))}
          <Button onClick={() => { setMobile(false); navigate(isStaff ? "/portal" : user ? "/" : "/login"); if (user && !isStaff) logout(); }} className="w-full bg-yellow-400 hover:bg-yellow-500 text-slate-900 mt-2">
            {isStaff ? "My Portal" : user ? "Sign Out" : "Sign In"}
          </Button>
        </div>
      )}
    </header>
  );
}
